import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Row,
  Col,
  Card,
  Button,
  ButtonGroup,
  Table,
  Badge,
  Form,
  Spinner,
  Alert,
  InputGroup
} from 'react-bootstrap';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend
} from 'chart.js';
import { Bar } from 'react-chartjs-2';
import moment from 'moment';
import 'moment/locale/it';
import { revenueService } from '../../services/api';
import { formatPaymentMethod } from '../../helpers/subscriptionHelpers';

// Register Chart.js components
ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend
);

const formatCurrency = (val) => {
  return new Intl.NumberFormat('it-IT', { style: 'currency', currency: 'EUR' }).format(val || 0);
};

const RevenueStatisticsTab = () => {
  moment.locale('it');

  // Quick filter presets: 'today' | 'week' | 'month' | 'year' | 'custom'
  const [activeFilter, setActiveFilter] = useState('month');

  // Date range
  const [startDate, setStartDate] = useState(moment().startOf('month').format('YYYY-MM-DD'));
  const [endDate, setEndDate] = useState(moment().endOf('month').format('YYYY-MM-DD'));

  // Data & Async
  const [revenueData, setRevenueData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // Table filters
  const [searchQuery, setSearchQuery] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [paymentMethodFilter, setPaymentMethodFilter] = useState('all');

  const setPreset = (preset) => {
    setActiveFilter(preset);
    const now = moment();
    switch (preset) {
      case 'today':
        setStartDate(now.format('YYYY-MM-DD'));
        setEndDate(now.format('YYYY-MM-DD'));
        break;
      case 'week':
        setStartDate(now.clone().startOf('isoWeek').format('YYYY-MM-DD'));
        setEndDate(now.clone().endOf('isoWeek').format('YYYY-MM-DD'));
        break;
      case 'month':
        setStartDate(now.clone().startOf('month').format('YYYY-MM-DD'));
        setEndDate(now.clone().endOf('month').format('YYYY-MM-DD'));
        break;
      case 'year':
        setStartDate(now.clone().startOf('year').format('YYYY-MM-DD'));
        setEndDate(now.clone().endOf('year').format('YYYY-MM-DD'));
        break;
      case 'custom':
        // Keep current custom dates
        break;
      default:
        break;
    }
  };

  const loadRevenue = useCallback(async () => {
    setLoading(true);
    setError('');
    const res = await revenueService.getRevenue(
      moment(startDate, 'YYYY-MM-DD').startOf('day').toDate(),
      moment(endDate, 'YYYY-MM-DD').endOf('day').toDate()
    );
    setLoading(false);

    if (res.success && res.data) {
      setRevenueData(res.data);
    } else {
      setError(res.message || 'Errore nel caricamento delle statistiche di incasso');
    }
  }, [startDate, endDate]);

  useEffect(() => {
    loadRevenue();
  }, [loadRevenue]);

  // Filtered transactions for the analytical table
  const filteredTransactions = useMemo(() => {
    if (!revenueData || !revenueData.transactions) return [];
    return revenueData.transactions.filter((tx) => {
      const matchCat = categoryFilter === 'all' || tx.category === categoryFilter;
      const matchPaymentMethod = paymentMethodFilter === 'all' || tx.paymentMethod === paymentMethodFilter;
      const matchSearch = !searchQuery.trim() ||
        tx.userName.toLowerCase().includes(searchQuery.toLowerCase()) ||
        tx.description.toLowerCase().includes(searchQuery.toLowerCase());
      return matchCat && matchPaymentMethod && matchSearch;
    });
  }, [revenueData, categoryFilter, paymentMethodFilter, searchQuery]);

  // Chart data configuration
  const chartData = useMemo(() => {
    if (!revenueData || !revenueData.timeline || revenueData.timeline.length === 0) {
      return { labels: [], datasets: [] };
    }

    const labels = revenueData.timeline.map((item) =>
      moment(item.date).format('DD/MM')
    );

    return {
      labels,
      datasets: [
        {
          label: 'Tesseramenti',
          data: revenueData.timeline.map((item) => item.memberships),
          backgroundColor: '#0d6efd',
          borderRadius: 4
        },
        {
          label: 'Abbonamenti',
          data: revenueData.timeline.map((item) => item.subscriptions),
          backgroundColor: '#198754',
          borderRadius: 4
        },
        {
          label: 'Noleggi',
          data: revenueData.timeline.map((item) => item.rentals),
          backgroundColor: '#fd7e14',
          borderRadius: 4
        },
        {
          label: 'Appuntamenti',
          data: revenueData.timeline.map((item) => item.appointments),
          backgroundColor: '#6f42c1',
          borderRadius: 4
        }
      ]
    };
  }, [revenueData]);

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'top',
        labels: { boxWidth: 12, font: { size: 12 } }
      },
      tooltip: {
        callbacks: {
          label: (ctx) => `${ctx.dataset.label}: ${formatCurrency(ctx.raw)}`
        }
      }
    },
    scales: {
      x: {
        stacked: true,
        grid: { display: false }
      },
      y: {
        stacked: true,
        ticks: {
          callback: (value) => `€${value}`
        }
      }
    }
  };

  const breakdown = revenueData?.breakdown;

  return (
    <div className="revenue-statistics-tab">
      {/* Quick Filters & Date Controls */}
      <Card className="mb-4 shadow-sm border-0 bg-light">
        <Card.Body className="py-3 px-3">
          <Row className="g-3 align-items-center">
            <Col xs={12} lg={6}>
              <div className="d-flex align-items-center flex-wrap gap-2">
                <span className="fw-semibold text-muted small me-2">Periodo Rapido:</span>
                <ButtonGroup size="sm">
                  <Button
                    variant={activeFilter === 'today' ? 'primary' : 'outline-primary'}
                    onClick={() => setPreset('today')}
                  >
                    Oggi
                  </Button>
                  <Button
                    variant={activeFilter === 'week' ? 'primary' : 'outline-primary'}
                    onClick={() => setPreset('week')}
                  >
                    Questa Settimana
                  </Button>
                  <Button
                    variant={activeFilter === 'month' ? 'primary' : 'outline-primary'}
                    onClick={() => setPreset('month')}
                  >
                    Questo Mese
                  </Button>
                  <Button
                    variant={activeFilter === 'year' ? 'primary' : 'outline-primary'}
                    onClick={() => setPreset('year')}
                  >
                    Quest&apos;Anno
                  </Button>
                  <Button
                    variant={activeFilter === 'custom' ? 'primary' : 'outline-primary'}
                    onClick={() => setActiveFilter('custom')}
                  >
                    Intervallo Libero
                  </Button>
                </ButtonGroup>
              </div>
            </Col>

            <Col xs={12} lg={6}>
              <div className="d-flex align-items-center gap-2 justify-content-lg-end">
                <Form.Group className="d-flex align-items-center gap-1 mb-0">
                  <Form.Label className="small mb-0 text-muted">Da:</Form.Label>
                  <Form.Control
                    type="date"
                    size="sm"
                    value={startDate}
                    onChange={(e) => {
                      setStartDate(e.target.value);
                      setActiveFilter('custom');
                    }}
                  />
                </Form.Group>
                <Form.Group className="d-flex align-items-center gap-1 mb-0">
                  <Form.Label className="small mb-0 text-muted">A:</Form.Label>
                  <Form.Control
                    type="date"
                    size="sm"
                    value={endDate}
                    onChange={(e) => {
                      setEndDate(e.target.value);
                      setActiveFilter('custom');
                    }}
                  />
                </Form.Group>
                <Button variant="outline-secondary" size="sm" onClick={loadRevenue} disabled={loading} title="Ricarica">
                  <i className={`fas fa-sync-alt ${loading ? 'fa-spin' : ''}`}></i>
                </Button>
              </div>
            </Col>
          </Row>
        </Card.Body>
      </Card>

      {error && <Alert variant="danger">{error}</Alert>}

      {/* 5 KPI Cards */}
      <Row className="g-3 mb-4">
        {/* Total Revenue */}
        <Col xs={12} sm={6} xl>
          <Card className="h-100 shadow-sm border-0 border-start border-4 border-primary">
            <Card.Body className="p-3">
              <div className="d-flex justify-content-between align-items-center">
                <div>
                  <small className="text-muted fw-semibold text-uppercase" style={{ fontSize: '0.72rem' }}>
                    Totale Incassato
                  </small>
                  <h4 className="fw-bold mb-0 text-primary mt-1">
                    {formatCurrency(revenueData?.totalRevenue)}
                  </h4>
                </div>
                <div className="bg-primary bg-opacity-10 rounded-circle p-3 text-primary">
                  <i className="fas fa-coins fa-lg"></i>
                </div>
              </div>
              <small className="text-muted mt-2 d-block">
                Solo incassi effettivi saldati
              </small>
            </Card.Body>
          </Card>
        </Col>

        {/* Memberships */}
        <Col xs={12} sm={6} xl>
          <Card className="h-100 shadow-sm border-0 border-start border-4 border-info">
            <Card.Body className="p-3">
              <div className="d-flex justify-content-between align-items-center">
                <div>
                  <small className="text-muted fw-semibold text-uppercase" style={{ fontSize: '0.72rem' }}>
                    Tesseramenti
                  </small>
                  <h4 className="fw-bold mb-0 text-dark mt-1">
                    {formatCurrency(breakdown?.memberships?.total)}
                  </h4>
                </div>
                <div className="bg-info bg-opacity-10 rounded-circle p-3 text-info">
                  <i className="fas fa-id-card fa-lg"></i>
                </div>
              </div>
              <small className="text-muted mt-2 d-block">
                {breakdown?.memberships?.count || 0} quote tesseramento
              </small>
            </Card.Body>
          </Card>
        </Col>

        {/* Subscriptions */}
        <Col xs={12} sm={6} xl>
          <Card className="h-100 shadow-sm border-0 border-start border-4 border-success">
            <Card.Body className="p-3">
              <div className="d-flex justify-content-between align-items-center">
                <div>
                  <small className="text-muted fw-semibold text-uppercase" style={{ fontSize: '0.72rem' }}>
                    Abbonamenti
                  </small>
                  <h4 className="fw-bold mb-0 text-success mt-1">
                    {formatCurrency(breakdown?.subscriptions?.total)}
                  </h4>
                </div>
                <div className="bg-success bg-opacity-10 rounded-circle p-3 text-success">
                  <i className="fas fa-tags fa-lg"></i>
                </div>
              </div>
              <small className="text-muted mt-2 d-block">
                {breakdown?.subscriptions?.count || 0} versamenti ({formatCurrency(breakdown?.subscriptions?.singlePaymentTotal)} unica / {formatCurrency(breakdown?.subscriptions?.installmentTotal)} rate)
              </small>
            </Card.Body>
          </Card>
        </Col>

        {/* Rentals */}
        <Col xs={12} sm={6} xl>
          <Card className="h-100 shadow-sm border-0 border-start border-4 border-warning">
            <Card.Body className="p-3">
              <div className="d-flex justify-content-between align-items-center">
                <div>
                  <small className="text-muted fw-semibold text-uppercase" style={{ fontSize: '0.72rem' }}>
                    Noleggi
                  </small>
                  <h4 className="fw-bold mb-0 text-dark mt-1">
                    {formatCurrency(breakdown?.rentals?.total)}
                  </h4>
                </div>
                <div className="bg-warning bg-opacity-10 rounded-circle p-3 text-warning">
                  <i className="fas fa-box-open fa-lg"></i>
                </div>
              </div>
              <small className="text-muted mt-2 d-block">
                {breakdown?.rentals?.count || 0} noleggi incassati
              </small>
            </Card.Body>
          </Card>
        </Col>

        {/* Appointments */}
        <Col xs={12} sm={6} xl>
          <Card className="h-100 shadow-sm border-0 border-start border-4 border-purple" style={{ borderLeftColor: '#6f42c1 !important' }}>
            <Card.Body className="p-3">
              <div className="d-flex justify-content-between align-items-center">
                <div>
                  <small className="text-muted fw-semibold text-uppercase" style={{ fontSize: '0.72rem' }}>
                    Appuntamenti
                  </small>
                  <h4 className="fw-bold mb-0 mt-1" style={{ color: '#6f42c1' }}>
                    {formatCurrency(breakdown?.appointments?.total)}
                  </h4>
                </div>
                <div className="rounded-circle p-3" style={{ backgroundColor: 'rgba(111, 66, 193, 0.1)', color: '#6f42c1' }}>
                  <i className="fas fa-calendar-check fa-lg"></i>
                </div>
              </div>
              <small className="text-muted mt-2 d-block">
                {breakdown?.appointments?.count || 0} lezioni saldate
              </small>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* Chart.js Timeline Section */}
      <Card className="mb-4 shadow-sm border-0">
        <Card.Header className="bg-white py-3 d-flex justify-content-between align-items-center">
          <h6 className="mb-0 fw-bold text-dark">
            <i className="fas fa-chart-bar me-2 text-primary"></i>
            Andamento Temporale Incassi per Categoria
          </h6>
          <small className="text-muted">
            {moment(startDate).format('DD/MM/YYYY')} - {moment(endDate).format('DD/MM/YYYY')}
          </small>
        </Card.Header>
        <Card.Body style={{ height: '320px' }}>
          {loading ? (
            <div className="d-flex justify-content-center align-items-center h-100">
              <Spinner animation="border" variant="primary" />
            </div>
          ) : !revenueData || !revenueData.timeline || revenueData.timeline.length === 0 ? (
            <div className="d-flex justify-content-center align-items-center h-100 text-muted">
              Nessun incasso registrato nel periodo temporale selezionato.
            </div>
          ) : (
            <Bar data={chartData} options={chartOptions} />
          )}
        </Card.Body>
      </Card>

      {/* Analytical Transactions Table */}
      <Card className="shadow-sm border-0">
        <Card.Header className="bg-white py-3">
          <Row className="g-2 align-items-center">
            <Col xs={12} lg={3}>
              <h6 className="mb-0 fw-bold text-dark text-truncate">
                <i className="fas fa-list-alt me-2 text-primary"></i>
                Dettaglio Analitico Movimenti ({filteredTransactions.length})
              </h6>
            </Col>
            <Col xs={12} sm={6} lg={3}>
              <InputGroup size="sm">
                <InputGroup.Text><i className="fas fa-search"></i></InputGroup.Text>
                <Form.Control
                  type="text"
                  placeholder="Cerca per atleta o causale..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                />
              </InputGroup>
            </Col>
            <Col xs={12} sm={6} lg={3}>
              <Form.Select
                size="sm"
                value={categoryFilter}
                onChange={(e) => setCategoryFilter(e.target.value)}
              >
                <option value="all">Tutte le Categorie</option>
                <option value="membership">Tesseramenti</option>
                <option value="subscription">Abbonamenti</option>
                <option value="rental">Noleggi</option>
                <option value="appointment">Appuntamenti</option>
              </Form.Select>
            </Col>
            <Col xs={12} sm={6} lg={3}>
              <Form.Select
                size="sm"
                value={paymentMethodFilter}
                onChange={(e) => setPaymentMethodFilter(e.target.value)}
              >
                <option value="all">Tutti i Metodi di Pagamento</option>
                <option value="cash">Contanti</option>
                <option value="bank_transfer">Bonifico</option>
                <option value="card">Carta</option>
                <option value="check">Assegno</option>
              </Form.Select>
            </Col>
          </Row>
        </Card.Header>
        <Card.Body className="p-0">
          {loading ? (
            <div className="text-center py-4">
              <Spinner animation="border" variant="primary" size="sm" />
              <span className="ms-2 text-muted small">Caricamento movimenti...</span>
            </div>
          ) : filteredTransactions.length === 0 ? (
            <div className="text-center py-5 text-muted">
              Nessuna transazione corrispondente ai filtri applicati.
            </div>
          ) : (
            <div className="table-responsive">
              <Table hover className="align-middle mb-0">
                <thead className="table-light small">
                  <tr>
                    <th style={{ width: '120px' }}>Data Incasso</th>
                    <th style={{ width: '130px' }}>Categoria</th>
                    <th style={{ width: '140px' }}>Metodo Pagamento</th>
                    <th>Atleta / Utente</th>
                    <th>Causale / Descrizione</th>
                    <th style={{ width: '120px' }} className="text-end">Importo</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredTransactions.map((tx) => {
                    let catBadge = <Badge bg="secondary">{tx.category}</Badge>;
                    if (tx.category === 'membership') catBadge = <Badge bg="info">Tesseramento</Badge>;
                    if (tx.category === 'subscription') catBadge = <Badge bg="success">Abbonamento</Badge>;
                    if (tx.category === 'rental') catBadge = <Badge bg="warning" text="dark">Noleggio</Badge>;
                    if (tx.category === 'appointment') catBadge = <Badge bg="purple" style={{ backgroundColor: '#6f42c1' }}>Appuntamento</Badge>;

                    return (
                      <tr key={tx.id}>
                        <td>
                          <small className="text-dark fw-semibold">
                            {moment(tx.paymentDate).format('DD/MM/YYYY')}
                          </small>
                          <small className="text-muted d-block" style={{ fontSize: '0.72rem' }}>
                            {moment(tx.paymentDate).format('HH:mm')}
                          </small>
                        </td>
                        <td>{catBadge}</td>
                        <td>
                          {tx.paymentMethod ? (
                            <Badge bg="light" text="dark" className="border" style={{ fontSize: '0.75rem' }}>
                              {formatPaymentMethod(tx.paymentMethod)}
                            </Badge>
                          ) : (
                            <span className="text-muted small">-</span>
                          )}
                        </td>
                        <td className="fw-semibold text-dark">{tx.userName}</td>
                        <td className="text-muted small">{tx.description}</td>
                        <td className="text-end fw-bold text-dark">
                          {formatCurrency(tx.amount)}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </Table>
            </div>
          )}
        </Card.Body>
      </Card>
    </div>
  );
};

export default RevenueStatisticsTab;
